/* eslint-disable react-hooks/rules-of-hooks */
/* eslint-disable @typescript-eslint/ban-ts-comment */
/* eslint-disable @typescript-eslint/no-explicit-any */
import type { ChangeEvent, FC } from 'react'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import ReactDOM from 'react-dom'

import TooltipContent from './tooltip-content'
import type { TooltipProps } from './types'
import useControlled from '../../hooks/use-controlled'
import useForkRef from '../../hooks/use-fork-ref'
import useIsFocusVisible from '../../hooks/use-is-focus-visible'
import type { Theme } from '../../theme-provider/types'
import { THEME_UI_DEFAULT_KEY } from '../../theme-provider/types'
import { useSafeThemeUI } from '../../theme-provider/use-theme-ui'
import deepmerge from '../../utils/deep-merge'
import { get } from '../../utils/object'
import { getSpace } from '../../utils/props-utils'
import setRef from '../../utils/set-ref'
import { useUniqueId } from '../../utils/unique-id'
import type { BoxProps } from '../_primitives/box/types'
import Grow from '../_utils/grow/grow'
import Popper from '../_utils/popper/popper'

const TOOLTIP = 'csg-tui-tooltip-'

// default arrow when arrow is true
export const arrowGenerator = {
  ['&[x-placement*="bottom"] .csg-tui-tooltip-arrow']: {
    top: 0,
    left: 0,
    marginTop: '-0.95em',
    marginLeft: 4,
    marginRight: 4,
    width: '2em',
    height: '1em',
    '&::before': {
      borderWidth: '0 1em 1em 1em',
      borderColor: 'transparent transparent currentcolor transparent'
    }
  },
  ['&[x-placement*="top"] .csg-tui-tooltip-arrow']: {
    bottom: 0,
    left: 0,
    marginBottom: '-0.95em',
    marginLeft: 4,
    marginRight: 4,
    width: '2em',
    height: '1em',
    '&::before': {
      borderWidth: '1em 1em 0 1em',
      borderColor: 'currentcolor transparent transparent transparent'
    }
  },
  ['&[x-placement*="right"] .csg-tui-tooltip-arrow']: {
    left: 0,
    marginLeft: '-0.95em',
    marginTop: 4,
    marginBottom: 4,
    height: '2em',
    width: '1em',
    '&::before': {
      borderWidth: '1em 1em 1em 0',
      borderColor: 'transparent currentcolor transparent transparent'
    }
  },
  ['&[x-placement*="left"]  .csg-tui-tooltip-arrow']: {
    right: 0,
    marginRight: '-0.95em',
    marginTop: 4,
    marginBottom: 4,
    height: '2em',
    width: '1em',
    '&::before': {
      borderWidth: '1em 0 1em 1em',
      borderColor: 'transparent transparent transparent currentcolor'
    }
  }
}

// bordered arrow (borderedArrow === true)
const borderedArrowGenerator = (
  backgroundColor?: BoxProps['bg'],
  borderColor?: string,
  boxShadow?: BoxProps['boxShadow'],
  border?: BoxProps['border'],
  scale?: number
) => {
  const size = `${14 * (scale ? +scale : 1)}px`
  const commonParentStyles = {
    width: size,
    height: size,
    position: 'absolute',
    overflow: 'hidden',
    pointerEvents: 'none'
  }
  const commonChildStyles = {
    content: '""',
    position: 'absolute',
    width: size,
    height: size,
    bg: backgroundColor,
    boxShadow: boxShadow,
    border:
      border ?? (borderColor ? `1px solid ${borderColor}` : '0px solid white'),
    boxSizing: 'border-box'
  }
  return {
    ['&[x-placement*="bottom"] .csg-tui-tooltip-arrow']: {
      top: 0,
      transform: 'translateY(-100%)',
      ...commonParentStyles,
      '&::before': {
        transform: `translateY(75%) rotate(45deg)`,
        ...commonChildStyles
      }
    },
    ['&[x-placement*="top"] .csg-tui-tooltip-arrow']: {
      bottom: 0,
      transform: 'translateY(100%)',
      ...commonParentStyles,
      '&::before': {
        transform: `translateY(-75%) rotate(45deg)`,
        ...commonChildStyles
      }
    },
    ['&[x-placement*="right"] .csg-tui-tooltip-arrow']: {
      left: 0,
      transform: 'translateX(-100%)',
      ...commonParentStyles,
      '&::before': {
        transform: `translateX(75%) rotate(45deg)`,
        ...commonChildStyles
      }
    },
    ['&[x-placement*="left"]  .csg-tui-tooltip-arrow']: {
      right: 0,
      transform: 'translateX(100%)',
      ...commonParentStyles,
      '&::before': {
        transform: `translateX(-75%) rotate(45deg)`,
        ...commonChildStyles
      }
    }
  }
}

const Tooltip: FC<TooltipProps> = React.forwardRef<
  HTMLDivElement,
  TooltipProps & { theme?: Theme }
>(function Tooltip(props, ref) {
  const theme = useSafeThemeUI(props)
  const {
    variant = THEME_UI_DEFAULT_KEY,
    border,
    arrow = false,
    borderedArrow = false,
    borderedArrowBackgroundColor = get(
      theme,
      `tooltips.${variant}.bg`,
      'white'
    ),
    borderedArrowBorderColor = undefined,
    arrowProps,
    children,
    disableFocusListener = false,
    disableHoverListener = false,
    disableTouchListener = false,
    enableWheelScrollListener = false,
    enterDelay = 100,
    enterNextDelay = 0,
    enterTouchDelay = 700,
    id: idProp,
    interactive = false,
    leaveDelay = 0,
    leaveTouchDelay = 1500,
    onClose,
    onOpen,
    open: openProp,
    placement = 'top-start',
    elevation = 300,
    PopperProps,
    title,
    TransitionComponent = Grow,
    TransitionProps,
    zIndex = 'tooltip',
    ...rest
  } = props

  const [childNode, setChildNode] = useState<HTMLInputElement>()
  const [arrowRef, setArrowRef] = useState<any>(null)
  const [hystersisOpen, setHystersisOpen] = useState(false)
  const ignoreNonTouchEvents = useRef(false)

  const closeTimer = useRef<any>()
  const enterTimer = useRef<any>()
  const leaveTimer = useRef<any>()
  const touchTimer = useRef<any>()
  const hystersisTimer = useRef<any>()

  const [openState, setOpenState] = useControlled({
    controlled: openProp,
    default: false,
    name: 'Tooltip',
    state: 'open'
  })

  const spaceProps = useMemo(() => getSpace(rest), [rest])

  let open = openState

  if (process.env.NODE_ENV !== 'production') {
    const { current: isControlled } = useRef(openProp !== undefined)

    useEffect(() => {
      if (
        childNode &&
        childNode.disabled &&
        !isControlled &&
        title !== '' &&
        childNode.tagName.toLowerCase() === 'button'
      ) {
        console.error(
          [
            'Tui: you are providing a disabled `button` child to the Tooltip component.',
            'A disabled element does not fire events.',
            "Tooltip needs to listen to the child element's events to display the title.",
            '',
            'Add a simple wrapper element, such as a `span`.'
          ].join('\n')
        )
      }
    }, [title, childNode, isControlled])
  }

  const id = idProp ? idProp : useUniqueId(TOOLTIP)

  useEffect(() => {
    return () => {
      clearTimeout(hystersisTimer.current)
      clearTimeout(closeTimer.current)
      clearTimeout(enterTimer.current)
      clearTimeout(leaveTimer.current)
      clearTimeout(touchTimer.current)
    }
  }, [])

  const handleOpen = useCallback(
    (event: ChangeEvent<Element>) => {
      clearTimeout(hystersisTimer.current)
      setHystersisOpen(true)
      // eslint-disable-next-line no-console
      // console.log(`handleOpen setOpenState(true)`)
      // The mouseover event will trigger for every nested element in the tooltip.
      // We can skip re-rendering when the tooltip is already open.
      // We are using the mouseover event instead of the mouseenter event to fix a hide/show issue.
      setOpenState(true)

      if (onOpen) {
        onOpen(event)
      }
    },
    [onOpen, setOpenState]
  )

  const handleEnter = useCallback(
    (forward = true) =>
      (event: ChangeEvent<HTMLElement>) => {
        const childrenProps = children.props
        // eslint-disable-next-line no-console
        // console.log(`handleEnter childNode: ${childNode}`)
        if (
          event.type === 'mouseover' &&
          childrenProps.onMouseOver &&
          forward
        ) {
          childrenProps.onMouseOver(event)
        }

        if (ignoreNonTouchEvents.current && event.type !== 'touchstart') {
          return
        }

        // Remove the title ahead of time.
        // We don't want to wait for the next render commit.
        // We would risk displaying two tooltips at the same time (native + this one).
        if (childNode) {
          childNode.removeAttribute('title')
        }

        clearTimeout(enterTimer.current)
        clearTimeout(leaveTimer.current)
        if (enterDelay || (hystersisOpen && enterNextDelay)) {
          event.persist()
          enterTimer.current = setTimeout(
            () => {
              handleOpen(event)
            },
            hystersisOpen ? enterNextDelay : enterDelay
          )
        } else {
          handleOpen(event)
        }
      },
    [
      childNode,
      children.props,
      enterDelay,
      enterNextDelay,
      handleOpen,
      hystersisOpen
    ]
  )

  const {
    isFocusVisible,
    onBlurVisible,
    ref: focusVisibleRef
  } = useIsFocusVisible()
  const [childIsFocusVisible, setChildIsFocusVisible] = useState(false)
  const handleBlur = useCallback(() => {
    if (childIsFocusVisible) {
      setChildIsFocusVisible(false)
      onBlurVisible()
    }
  }, [childIsFocusVisible, onBlurVisible])

  const handleFocus = useCallback(
    (forward = true) =>
      (event: ChangeEvent<HTMLInputElement>) => {
        // Workaround for https://github.com/facebook/react/issues/7769
        // The autoFocus of React might trigger the event before the componentDidMount.
        // We need to account for this eventuality.
        if (!childNode) {
          setChildNode(event.currentTarget)
        }

        if (isFocusVisible(event)) {
          setChildIsFocusVisible(true)
          handleEnter()(event)
        }

        const childrenProps = children.props
        if (childrenProps.onFocus && forward) {
          childrenProps.onFocus(event)
        }
      },
    [childNode, children.props, handleEnter, isFocusVisible]
  )

  const handleClose = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      clearTimeout(hystersisTimer.current)
      hystersisTimer.current = setTimeout(() => {
        setHystersisOpen(false)
      }, 800 + leaveDelay)
      // eslint-disable-next-line no-console
      // console.log(`handleClose setOpenState(false)`)
      setOpenState(false)

      if (onClose) {
        onClose(event)
      }

      clearTimeout(closeTimer.current)
      closeTimer.current = setTimeout(
        () => {
          ignoreNonTouchEvents.current = false
        },
        get(theme, 'transitions.duration.shortest', 150)
      )
    },
    [leaveDelay, onClose, setOpenState, theme]
  )

  const handleLeave = useCallback(
    (forward = true) =>
      (event: ChangeEvent<HTMLInputElement>) => {
        const childrenProps = children.props
        // eslint-disable-next-line no-console
        // console.log(`handleLeave childNode: ${event.type}`)
        if (event.type === 'blur') {
          if (childrenProps.onBlur && forward) {
            childrenProps.onBlur(event)
          }
          handleBlur()
        }

        if (
          event.type === 'mouseleave' &&
          childrenProps.onMouseLeave &&
          event.currentTarget === childNode
        ) {
          childrenProps.onMouseLeave(event)
        }

        clearTimeout(enterTimer.current)
        clearTimeout(leaveTimer.current)
        event.persist()
        leaveTimer.current = setTimeout(() => {
          handleClose(event)
        }, leaveDelay)
      },
    [childNode, children.props, handleBlur, handleClose, leaveDelay]
  )

  const handleTouchStart = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      ignoreNonTouchEvents.current = true
      const childrenProps = children.props

      if (childrenProps.onTouchStart) {
        childrenProps.onTouchStart(event)
      }

      clearTimeout(leaveTimer.current)
      clearTimeout(closeTimer.current)
      clearTimeout(touchTimer.current)
      event.persist()
      touchTimer.current = setTimeout(() => {
        handleEnter()(event)
      }, enterTouchDelay)
    },
    [children.props, enterTouchDelay, handleEnter]
  )

  const handleTouchEnd = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      if (children.props.onTouchEnd) {
        children.props.onTouchEnd(event)
      }

      clearTimeout(touchTimer.current)
      clearTimeout(leaveTimer.current)
      event.persist()
      leaveTimer.current = setTimeout(() => {
        handleClose(event)
      }, leaveTouchDelay)
    },
    [children.props, handleClose, leaveTouchDelay]
  )

  const handleUseRef = useForkRef(setChildNode, ref as any)
  const handleFocusRef = useForkRef(focusVisibleRef, handleUseRef)
  // can be removed once we drop support for non ref forwarding class components
  const handleOwnRef = useCallback(
    (instance?: React.ReactInstance | null) => {
      // #StrictMode ready
      // eslint-disable-next-line react/no-find-dom-node
      setRef(handleFocusRef, ReactDOM.findDOMNode(instance))
    },
    [handleFocusRef]
  )
  // @ts-ignore
  const handleRef = useForkRef(children.ref, handleOwnRef)

  // There is no point in displaying an empty tooltip.
  if (title === '') {
    open = false
  }

  // For accessibility and SEO concerns, we render the title to the DOM node when
  // the tooltip is hidden. However, we have made a tradeoff when
  // `disableHoverListener` is set. This title logic is disabled.
  // It's allowing us to keep the implementation size minimal.
  // We are open to change the tradeoff.
  const shouldShowNativeTitle = !open && !disableHoverListener
  const childrenProps = {
    'aria-describedby': open ? id : null,
    title: shouldShowNativeTitle && typeof title === 'string' ? title : null,
    ...children.props,
    ref: handleRef
  }

  const interactiveWrapperListeners: any = {}

  if (!disableTouchListener) {
    childrenProps.onTouchStart = handleTouchStart
    childrenProps.onTouchEnd = handleTouchEnd
  }

  if (enableWheelScrollListener) {
    childrenProps.onWheel = handleLeave()
    if (interactive) {
      interactiveWrapperListeners.onWheel = handleLeave(false)
    }
  }

  if (!disableHoverListener) {
    childrenProps.onMouseOver = handleEnter()
    // childrenProps.onMouseLeave = handleLeave()  // original code
    // update to onMouseOut because onMouseLeave was not working when using a none input Html element
    // to support disable button scenario. wait and see if there are other side-effects from this change.
    childrenProps.onMouseOut = handleLeave()

    if (interactive) {
      interactiveWrapperListeners.onMouseOver = handleEnter(false)
      interactiveWrapperListeners.onMouseLeave = handleLeave(false)
    }
  }

  if (!disableFocusListener) {
    childrenProps.onFocus = handleFocus()
    childrenProps.onBlur = handleLeave()

    if (interactive) {
      interactiveWrapperListeners.onFocus = handleFocus(false)
      interactiveWrapperListeners.onBlur = handleLeave(false)
    }
  }

  if (process.env.NODE_ENV !== 'production') {
    if (children.props.title) {
      console.error(
        [
          'Tui: you have provided a `title` prop to the child of <Tooltip />.',
          `Remove this title prop \`${children.props.title}\` or the Tooltip component.`
        ].join('\n')
      )
    }
  }

  const mergedPopperProps = useMemo(() => {
    return arrowRef
      ? deepmerge(
          {
            popperOptions: {
              modifiers: {
                arrow: {
                  enabled: Boolean(arrowRef),
                  element: arrowRef
                }
              }
            }
          },
          PopperProps
        )
      : PopperProps
  }, [arrowRef, PopperProps])

  const mergedArrowProps = useMemo(() => {
    if (arrow || borderedArrow || arrowProps) {
      return borderedArrowGenerator(
        arrowProps?.bg ?? borderedArrowBackgroundColor,
        borderedArrowBorderColor,
        arrowProps?.boxShadow ??
          get(theme, `tooltips.${variant}.boxShadow`, undefined),
        arrowProps?.border,
        arrowProps?.scale
      )
    } else return null
  }, [
    arrowProps,
    arrow,
    borderedArrow,
    borderedArrowBackgroundColor,
    borderedArrowBorderColor,
    variant,
    theme
  ])

  const basePopperStyle = useMemo(
    () => ({
      zIndex,
      pointerEvents: interactive ? 'auto' : 'none',
      ...(mergedArrowProps ?? {}),
      ...(typeof title !== 'string' ? { ['.csg-tui-pane']: spaceProps } : {})
    }),
    [interactive, mergedArrowProps, spaceProps, title, zIndex]
  )

  // eslint-disable-next-line no-console
  // console.log(
  //   `rendering tooltip open: ${open} shouldShowNativeTitle: ${shouldShowNativeTitle}`
  // )
  return (
    <React.Fragment>
      {React.cloneElement(children, childrenProps)}
      <Popper
        className="csg-tui-tooltip"
        placement={placement}
        anchorEl={childNode}
        open={childNode ? open : false}
        id={childrenProps['aria-describedby']}
        transition
        {...interactiveWrapperListeners}
        {...mergedPopperProps}
        __css={basePopperStyle}
      >
        {({
          placement: placementInner,
          TransitionProps: TransitionPropsInner
        }) => (
          <TooltipContent
            variant={variant}
            Component={TransitionComponent}
            title={title}
            border={border}
            elevation={elevation}
            placement={placementInner}
            TransitionProps={TransitionProps}
            ComponentTransitionProps={TransitionPropsInner}
            arrow={!!mergedArrowProps}
            ignoreNonTouchEvents={ignoreNonTouchEvents}
            setArrowRef={setArrowRef}
            {...rest}
          />
        )}
      </Popper>
    </React.Fragment>
  )
})

export default Tooltip
